/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
// types
import {
    MROChildCart,
    MROChildCartState,
    MROChildLocalCart,
    MRONativeLocalCart,
    ReviewedCartApiResponse,
    MROCartSelectionMode,
} from "@swiggy-private/cart-types";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
// utils
import { cloneDeep, isEmpty } from "@swiggy-private/js-utils";
import {
    getCartMenuItems,
    getCouponCode,
    getSelectedAddressFromReviewedCart,
    getSelectedDropOffInstructionIds,
} from "./Cart";
import { buildCartJourneyContext } from "./CartJourneyContext";
import {
    getMROChildCartRestaurantId,
    getMROChildCartRestaurantName,
    isEmptyMROCart,
    isRXPresentInSelectedList,
    normaliseId,
    logMROEventsIntoNR,
} from "./MROFoodCart";
// constants
import { MRO_DEFAULT_EMPTY_ARRAY_PLACEHOLDER } from "@cart-client/constants/MROConstants";

const logMROPayloadErrors = (error: string) => {
    logMROEventsIntoNR({
        type: "mro_cart_payload_prep_error",
        error,
    });
    throw new Error(error);
};

/**
 * used to filter out the selected carts from the local cart for the update-cart API call
 */
export const filterSelectedCartsForUpdateCartCallsFromLocalCart = (
    localCart: MRONativeLocalCart,
): MRONativeLocalCart => {
    const childCartClone = cloneDeep(localCart?.child_carts) || [];
    // filter out only selected carts which have non empty cart items
    const selectedCarts = childCartClone.filter((childCart: MROChildLocalCart) => {
        return childCart.cart_state === MROChildCartState.SELECTED && !isEmpty(childCart?.cartItems);
    });

    return {
        ...localCart,
        child_carts: selectedCarts,
    };
};

/**
 * USECASE A: When app GET_LOCAL_CART payload is used to initialise the MRO cart
 *
 * This is an internal method written to work on child_carts coming from GET_LOCAL_CART and operate on them for update-cart API call
 */
const _prepareUpdateMROChildCartPayloadFromGetLocalCart = (
    childCarts: MROChildLocalCart[],
    selectedRXIds: string[],
): MROChildLocalCart[] => {
    const updatedChildCarts: MROChildLocalCart[] = [];
    let selectedCartCounts = 0;
    let unselectedCartCounts = 0;
    childCarts.forEach((childCart: MROChildLocalCart) => {
        const isCartSelected = Boolean(
            isRXPresentInSelectedList(selectedRXIds, normaliseId(getMROChildCartRestaurantId(childCart))),
        );
        const cartState = isCartSelected ? MROChildCartState.SELECTED : MROChildCartState.UNSELECTED;
        updatedChildCarts.push({
            originalCartId: childCart?.originalCartId,
            cartId: childCart?.cartId,
            cart_state: cartState,
            sld: childCart?.sld,
            created_at: childCart?.created_at,
            parent_restaurant_id: childCart?.parent_restaurant_id,
            restaurantId: getMROChildCartRestaurantId(childCart),
            restaurantName: getMROChildCartRestaurantName(childCart),
            cart_saved_preferences: childCart?.cart_saved_preferences,
            flowContext: childCart?.flowContext,
            delivery_type_opt_in: childCart?.delivery_type_opt_in,
            orderComment: childCart?.orderComment,
            cutleryComments: childCart?.cutleryComments,
            p_segment_id: childCart?.p_segment_id,
            customer_selected_sla_option: childCart?.customer_selected_sla_option,
            selected_sla_option: childCart?.selected_sla_option,
            couponCode: childCart?.couponCode,
            cart_update_coupon_request: childCart?.cart_update_coupon_request,
            cartItems: childCart?.cartItems,
            mealIds: childCart?.mealIds,
            cart_continuity_state_attributes: childCart?.cart_continuity_state_attributes,
        } as MROChildLocalCart);
        if (isCartSelected) {
            selectedCartCounts++;
        } else {
            unselectedCartCounts++;
        }
    });

    // NOTE Special Case:
    // We are allowing partial selection of carts for Native MRO cart.
    // User selected 2 out of 4 carts on cart page and it was synced into the native cart
    // Now webview refreshed and the cart is refetched from native in such cases we will have partial carts from apps with no prefetch

    if (selectedCartCounts === 0 || unselectedCartCounts === childCarts.length) {
        logMROPayloadErrors("No selected carts found for Native MRO cart");
    } else if (selectedCartCounts + unselectedCartCounts !== childCarts.length) {
        logMROPayloadErrors("Selected and unselected carts do not match total Native MRO child carts");
    } else if (selectedCartCounts > childCarts.length) {
        logMROPayloadErrors("Selected carts are greater than the number of Native MRO child carts");
    } else if (unselectedCartCounts >= childCarts.length) {
        logMROPayloadErrors("Unselected carts are equal/greater than the number of Native MRO child carts");
    }
    return updatedChildCarts;
};

/**
 * USECASE A: When app GET_LOCAL_CART payload is used to initialise the MRO cart
 *
 * This is an orchestrator method written to work on the local cart and prepare the update-cart API payload.
 */
export const prepareUpdateMROCartPayloadFromNativeGetLocalCart = (
    localCart: MRONativeLocalCart,
): MRONativeLocalCart => {
    // validate the local cart
    if (isEmpty(localCart) || isEmptyMROCart(localCart)) {
        logMROPayloadErrors("No local cart found for MRO cart from apps");
    }
    // apps send us the list of selected RX ids from discovery to cart
    const selectedRXIds = localCart?.selected_rxids || [];
    if (isEmpty(selectedRXIds)) {
        logMROPayloadErrors("No selected RX ids found for MRO cart from apps");
    }
    return {
        originalCartId: localCart?.originalCartId,
        cartId: localCart?.cartId,
        address_nudge_check: localCart?.address_nudge_check,
        cart_journey_ctx: localCart?.cart_journey_ctx,
        cartLatLong: localCart?.cartLatLong,
        date: localCart?.date,
        tipDetails: localCart?.tipDetails,
        selected_drop_off_instruction_ids: localCart?.selected_drop_off_instruction_ids,
        address_id: localCart?.address_id,
        cart_type: localCart?.cart_type,
        userLiveLat: localCart?.userLiveLat,
        userLiveLng: localCart?.userLiveLng,
        couponCode: localCart?.couponCode,
        cart_update_coupon_request: localCart?.cart_update_coupon_request,
        flowContext: localCart?.flowContext,
        first_time_user: localCart?.first_time_user,
        is_incognito_cart: localCart?.is_incognito_cart,
        cart_initialization_source: localCart?.cart_initialization_source,
        cart_category_type: localCart?.cart_category_type,
        selected_rxids: localCart?.selected_rxids,
        child_carts: _prepareUpdateMROChildCartPayloadFromGetLocalCart(localCart?.child_carts ?? [], selectedRXIds),
    };
};

/**
 * USECASE B: MRO CHILD CART SYNC FROM SERVER + LOCAL CARTS
 *
 * This is an internal method written to work on the server cart and local cart and prepare the internal child cart list.
 */
const _prepareInternalChildCarts = (
    childCarts: MROChildCart[],
    selectedRXIds: string[],
    localCart?: MRONativeLocalCart,
    disableServerLenCheckForPrefetchResponse = false,
): MROChildLocalCart[] => {
    const internalChildCartList: MROChildLocalCart[] = [];
    // mismatch detected in carts returned from server vs user selected carts in local
    const selectedRxIdPresentInServerCart = selectedRXIds.every((selectedRXId: string) => {
        return childCarts.some(
            (childCart: MROChildCart) => getMROChildCartRestaurantId(childCart) === normaliseId(selectedRXId),
        );
    });
    if (isEmpty(selectedRXIds) || !selectedRxIdPresentInServerCart) {
        logMROPayloadErrors("selected rx ids empty or not present in server cart");
    }
    // sanity check counters
    let selectedCartCount = 0;
    let unselectedCartCount = 0;
    localCart?.child_carts?.map((localChildCart: MROChildLocalCart) => {
        // find the server child cart for the local child cart
        const localRxId = getMROChildCartRestaurantId(localChildCart);
        const serverChildCart: MROChildCart | undefined = childCarts?.find(
            (child: MROChildCart) => getMROChildCartRestaurantId(child) === localRxId,
        );
        // check if the same local child cart is selected
        const selected = Boolean(isRXPresentInSelectedList(selectedRXIds, localRxId));

        // if the server child cart is found and corresponding local cart was selected,
        // then add it to the internal child cart list
        if (!isEmpty(serverChildCart) && selected) {
            // Treat each server child cart like a BAU server cart. Resolve the applied
            // delivery address the same way the BAU flow does: prefer the address marked
            // `selected`, and fall back to the first address when the flag is absent.
            const serverSelectedAddress =
                getSelectedAddressFromReviewedCart(serverChildCart as unknown as ReviewedCartApiResponse) ??
                serverChildCart?.addresses?.[0];
            internalChildCartList.push({
                cart_state: MROChildCartState.SELECTED,
                // local cart sync
                created_at: localChildCart?.created_at,
                parent_restaurant_id: localChildCart?.parent_restaurant_id,
                sld: localChildCart?.sld,
                p_segment_id: localChildCart?.p_segment_id,
                orderComment: localChildCart?.orderComment,
                cutleryComments: localChildCart?.cutleryComments,
                flowContext: localChildCart?.flowContext,
                restaurantId:
                    getMROChildCartRestaurantId(localChildCart) || getMROChildCartRestaurantId(serverChildCart),
                restaurantName:
                    getMROChildCartRestaurantName(localChildCart) || getMROChildCartRestaurantName(serverChildCart),
                mealIds: localChildCart?.mealIds,
                // server cart sync
                originalCartId: serverChildCart?.originalCartId,
                cartId: serverChildCart?.cartId,
                cart_total: serverChildCart?.cart_total,
                order_total: serverChildCart?.order_total,
                cart_saved_preferences: serverChildCart?.cart_saved_preferences,
                // Match BAU coupon resolution (coupon_details.coupon_codes.restaurant_coupon → coupon_code).
                couponCode: getCouponCode(serverChildCart as unknown as ReviewedCartApiResponse),
                cart_update_coupon_request: serverChildCart?.cart_update_coupon_request,

                // Canonical local-cart field name is `isCouponValid` (see NativeLocalCart).
                isCouponValid: serverChildCart?.is_coupon_valid,
                // Delivery speed + opt-in come from the applied address, not blindly addresses[0].
                delivery_type_opt_in:
                    serverSelectedAddress?.delivery_type_opt_in ?? localChildCart?.delivery_type_opt_in,
                selected_sla_option: serverSelectedAddress?.selected_sla_option,
                customer_selected_sla_option: localChildCart?.customer_selected_sla_option || "",
                restaurantCloudinaryImageId: serverChildCart.restaurant_details?.cloudinary_image_id,
                selectedAddress: serverSelectedAddress,
                cartItems: getCartMenuItems(serverChildCart as unknown as ReviewedCartApiResponse),
                cart_continuity_state_attributes: serverChildCart?.cart_continuity_state_attributes,
            });
            selectedCartCount++;
        } else {
            // if the server child cart is not found, then add the local child cart to the internal child cart list
            // but mark it as UNSELECTED as server only gets and sends back the selected carts

            // EDGE CASE: prefetch with partial selection mode
            // in this case app send selected_rxids = [1,2] when we have 3 carts while the prefetch response has all 3 carts
            // now as the top level check looks up in selected_rxids for selection we were skipping populating order_total for unselected carts
            // so in this else we need to set the order total from the server cart even for unselected carts
            let orderTotal = localChildCart?.order_total ?? 0;
            let cartContinuityStateAttributes = localChildCart?.cart_continuity_state_attributes;
            const seedCriticalServerCartData = disableServerLenCheckForPrefetchResponse && !isEmpty(serverChildCart);
            if (seedCriticalServerCartData) {
                orderTotal = serverChildCart?.order_total ?? 0;
                cartContinuityStateAttributes = serverChildCart?.cart_continuity_state_attributes;
            }

            internalChildCartList.push({
                ...localChildCart,
                order_total: orderTotal,
                cart_continuity_state_attributes: cartContinuityStateAttributes,
                cart_state: MROChildCartState.UNSELECTED,
            });
            unselectedCartCount++;
        }
    });

    if (selectedCartCount + unselectedCartCount !== localCart?.child_carts?.length) {
        logMROPayloadErrors("Number of selected and unselected carts does not match the number of local carts");
    } else if (selectedCartCount === 0 || unselectedCartCount === localCart?.child_carts?.length) {
        logMROPayloadErrors("No selected carts found for MRO cart");
    } else if (selectedCartCount !== selectedRXIds.length) {
        logMROPayloadErrors("Number of selected carts does not match the number of selected RX ids");
    } else if (!disableServerLenCheckForPrefetchResponse && selectedCartCount !== childCarts.length) {
        logMROPayloadErrors("Number of selected carts does not match the number of server carts");
    } else if (selectedCartCount > localCart?.child_carts?.length) {
        logMROPayloadErrors("Number of selected carts is greater than the number of local carts");
    } else if (unselectedCartCount >= localCart?.child_carts?.length) {
        logMROPayloadErrors("Number of unselected carts is equal/greater than the number of local carts");
    }

    return internalChildCartList;
};

/**
 * Seeds each UNSELECTED native child cart with its `order_total` read from an
 * all-carts server response (the prefetch first call, which is the only response
 * that contains every child cart).
 *
 * Why: the follow-up single/partial update-cart call rebuilds the internal local
 * cart from the RAW native cart + a response that omits unselected carts. Without
 * this seed, the unselected tab's total is lost and renders as 0. Seeding is inert
 * for the update-cart payload prep, which never reads `order_total`.
 */
export const seedCriticalServerCartDataFromAllCartsForPartialViews = (
    localCart: MRONativeLocalCart,
    allCartsResponse?: MROReviewedCart | null,
): MRONativeLocalCart => {
    const serverChildCarts = allCartsResponse?.data?.child_carts ?? [];
    if (isEmpty(serverChildCarts) || isEmptyMROCart(localCart)) {
        return localCart;
    }
    const selectedRXIds = localCart?.selected_rxids ?? [];
    return {
        ...localCart,
        child_carts: localCart?.child_carts?.map((child: MROChildLocalCart) => {
            const rxId = getMROChildCartRestaurantId(child);
            // selected carts already get their total from the server child cart, skip them
            if (isRXPresentInSelectedList(selectedRXIds, rxId)) {
                return child;
            }
            const serverChild = serverChildCarts.find(
                (server: MROChildCart) => getMROChildCartRestaurantId(server) === rxId,
            );
            if (isEmpty(serverChild) || !serverChild?.order_total) {
                return child;
            }
            return {
                ...child,
                order_total: serverChild.order_total,
                cart_continuity_state_attributes: serverChild?.cart_continuity_state_attributes,
            };
        }),
    };
};

/**
 * Single place where the background-refresh payload back-syncs child-cart values that only the
 * RAW native cart knows about. Add every future field of this kind here.
 *
 * Why: a prefetched response can be older than the native cart — that staleness is exactly why
 * a background refresh is armed. Hydrating from it overwrites native values with the older
 * cached ones, so the background-refresh update-cart silently sends stale intent. Both carts
 * hold server-resolved values, the native one is simply the more recent copy.
 *
 * Handled today — restaurant coupon: apps back-fill both apply and remove into the native cart,
 * so it is the truth here and is mirrored absence included. A gap check would miss the two other
 * cases: a coupon swapped for another one, and a coupon removed while the cache still shows it.
 *
 * Booleans and amounts (incognito, tip) do NOT belong here: `isEmpty(false)` is `true`, so an
 * explicit "off" reads as missing and would be flipped back on.
 */
export const backSyncNativeLocalCartPayloadForBgRefresh = (
    localCart: MRONativeLocalCart,
    nativeLocalCart?: MRONativeLocalCart | null,
): MRONativeLocalCart => {
    const nativeChildCarts = nativeLocalCart?.child_carts ?? [];
    if (isEmpty(nativeChildCarts) || isEmptyMROCart(localCart)) {
        return localCart;
    }
    return {
        ...localCart,
        child_carts: localCart?.child_carts?.map((child: MROChildLocalCart) => {
            const rxId = getMROChildCartRestaurantId(child);
            const nativeChild = nativeChildCarts.find(
                (native: MROChildLocalCart) => getMROChildCartRestaurantId(native) === rxId,
            );
            if (isEmpty(nativeChild)) {
                return child;
            }
            // Mirroring coupon absence is only safe on a complete child cart. A partial bridge
            // payload (iOS can hand back child carts without items) must never wipe a live coupon.
            const canMirrorNativeCoupon = !isEmpty(nativeChild?.cartItems);
            return {
                ...child,
                ...(canMirrorNativeCoupon && {
                    couponCode: nativeChild?.couponCode,
                    cart_update_coupon_request: nativeChild?.cart_update_coupon_request,
                }),
            };
        }),
    };
};

/**
 * USECASE B: MRO WEB LOCAL CART SYNC FROM SERVER + LOCAL CARTS
 *
 * This is an orchestrator method written to work on the server cart and local cart and prepare the web local cart payload.
 */
export const prepareInternalWebLocalCartPayloadFromServerAndLocalCart = (
    cartResponse?: MROReviewedCart | null,
    localCart?: MRONativeLocalCart,
    disableServerLenCheckForPrefetchResponse = false,
): MRONativeLocalCart => {
    if (isEmptyMROCart(localCart)) {
        logMROPayloadErrors("No local child cart found for MRO");
    } else if (isEmptyMROCart(cartResponse?.data)) {
        logMROPayloadErrors("No server response with child carts data found for MRO");
    }

    const selectedRXIds = localCart?.selected_rxids ?? [];
    const serverChildCarts = cartResponse?.data?.child_carts ?? [];
    if (isEmpty(serverChildCarts)) {
        logMROPayloadErrors("No server child carts found for MRO");
    } else if (isEmpty(selectedRXIds)) {
        logMROPayloadErrors("No selected RX ids found for MRO cart in local cart");
    }

    const computedChildCartsForMRO = _prepareInternalChildCarts(
        serverChildCarts,
        selectedRXIds,
        localCart,
        disableServerLenCheckForPrefetchResponse,
    );
    const computedDropoffInstructionIds = getSelectedDropOffInstructionIds(cartResponse?.data?.drop_off_instructions);
    // Resolve the applied delivery address the BAU way: prefer the address marked
    // `selected`, fall back to the first address when the flag is absent.
    const parentSelectedAddress =
        getSelectedAddressFromReviewedCart(cartResponse?.data as ReviewedCartApiResponse) ??
        cartResponse?.data?.addresses?.[0];

    if (isEmpty(computedChildCartsForMRO)) {
        logMROPayloadErrors("No child carts found for MRO");
    }

    return {
        // static
        subscriptionItems: MRO_DEFAULT_EMPTY_ARRAY_PLACEHOLDER, // swiggy one purchase is disabled in MRO
        // server cart values backsynced
        originalCartId: cartResponse?.data?.originalCartId,
        cartId: cartResponse?.data?.cartId,
        address_id: cartResponse?.data?.address_id,
        tipDetails: cartResponse?.data?.tipDetails,
        selectedAddress: parentSelectedAddress,
        is_incognito_cart: cartResponse?.data?.is_incognito_cart,
        cart_type: cartResponse?.data?.cart_type,
        selected_drop_off_instruction_ids: computedDropoffInstructionIds,
        // Order-wide alliance coupon. MRO does not support payment coupons, so we simply
        // back-sync the server's coupon request into local storage and re-send it as-is on
        // the next update-cart, instead of rebuilding the payload inline every time.
        couponCode: cartResponse?.data?.couponCode,
        cart_update_coupon_request: cartResponse?.data?.cart_update_coupon_request,
        // Renderer reads localCart.isCouponValid for the coupon-applied impression.
        isCouponValid: cartResponse?.data?.is_coupon_valid,
        // synced from local cart
        // Normalise the journey context against the server response (honours cart_context_drop).
        cart_journey_ctx: buildCartJourneyContext(localCart?.cart_journey_ctx, cartResponse?.data ?? undefined),
        selected_rxids: localCart?.selected_rxids,
        // MovieBeam (and every other flow) treats flowContext as an app-provided passthrough.
        flowContext: localCart?.flowContext,
        date: localCart?.date,
        cart_initialization_source: localCart?.cart_initialization_source,
        cart_category_type: localCart?.cart_category_type,
        cartLatLong: localCart?.cartLatLong,
        address_nudge_check: localCart?.address_nudge_check,
        first_time_user: localCart?.first_time_user,
        userLiveLat: localCart?.userLiveLat,
        userLiveLng: localCart?.userLiveLng,
        child_carts: computedChildCartsForMRO,
    };
};

/**
 * Used to determine the user selected cart selection mode from the local cart or prefetch response
 */
export const fetchUserSelectedCartSelectionMode = (nativeLocalCartData?: MRONativeLocalCart): MROCartSelectionMode => {
    const selectedRXIds = nativeLocalCartData?.selected_rxids ?? [];
    if (isEmpty(selectedRXIds)) {
        logMROPayloadErrors("No selected RX ids found for MRO cart in local cart for mode determination");
    }

    // compare each selected rxid with each child cart with no duplicates or mismatch
    const selectedRXIdsSet = new Set(selectedRXIds);
    const childCartsSet = new Set(
        nativeLocalCartData?.child_carts?.map((childCart: MROChildLocalCart) => getMROChildCartRestaurantId(childCart)),
    );

    let mode = MROCartSelectionMode.DEFAULT;
    if (selectedRXIdsSet.size === childCartsSet.size) {
        mode = MROCartSelectionMode.ALL;
    } else if (selectedRXIdsSet.size === 1) {
        mode = MROCartSelectionMode.SINGLE;
    } else if (selectedRXIdsSet.size < childCartsSet.size) {
        // if the selected RX ids are less than the child carts, then it is a partial RX view
        // this is an edge case we are enabling for webview refresh cases where user did partial selection and webview refreshed on cart
        mode = MROCartSelectionMode.PARTIAL;
    } else {
        logMROPayloadErrors("Invalid selected RX ids found for MRO cart in local cart");
    }

    logMROEventsIntoNR({
        type: "mro_native_local_cart_selection_mode_determined",
        mode: mode,
        selectedRXIds: JSON.stringify(Array.from(selectedRXIdsSet)),
        childCarts: JSON.stringify(Array.from(childCartsSet)),
    });

    return mode;
};
